// Общий чат — mesaje live prin WebSocket.
//
// Trei lucruri care despart un chat folosibil de o listă de mesaje:
//   • linia «непрочитанное» — vezi dintr-o privire unde ai rămas data trecută;
//   • derularea se lipește de jos doar dacă erai deja jos — altfel un mesaj nou te-ar
//     smulge din locul unde citeai;
//   • o singură reacție de persoană, ca la ScalpX: clicul pe alta o înlocuiește.

import { el, clear, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { pictAvatar } from '../ui/avatars.js';
import { session, initials } from '../core/session.js';
import { openAuth } from '../ui/auth-modal.js';
import { guestGate } from '../ui/guest-gate.js';
import { toast } from '../ui/components.js';

const EMOJI = ['👍', '🔥', '😀', '😮', '😢', '💩', '🚀', '❤️'];
/** Sub atâția pixeli de margine considerăm că utilizatorul e „jos". */
const STICK_PX = 60;

function timeLabel(ts) {
  return new Date(ts * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(ts) {
  const d = new Date(ts * 1000);
  const azi = new Date();
  const ieri = new Date(azi.getTime() - 86400e3);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, azi)) return 'Сегодня';
  if (same(d, ieri)) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

export class ChatSection {
  constructor(app) {
    this.app = app;
    this.messages = [];
    this.lastRead = 0;
    // Camera deschisa. Gol inseamna „общий". Mesajele poarta deja moneda de cand se
    // trimit din screener, deci camerele nu cer nici tabel nou, nici socket nou:
    // aceeasi conexiune, alt filtru.
    this.camera = new URLSearchParams(location.search).get('symbol') || '';
  }

  async mount(host) {
    this.root = el('div', { class: 'chat-section' });

    this.onlineEl = el('span', { class: 'chat-online', text: '—' });
    this.listEl = el('div', {
      class: 'chat-list',
      onscroll: () => this.#onScroll(),
    });
    this.jumpEl = el('button', {
      class: 'chat-jump hidden',
      onclick: () => this.#scrollToBottom(true),
    }, [icon('arrowDown', 12), el('span', { text: 'К непрочитанным' })]);

    this.inputEl = el('textarea', {
      class: 'chat-input',
      rows: 1,
      placeholder: 'Написать сообщение…',
      oninput: () => this.#autoGrow(),
      onkeydown: (e) => {
        // Enter trimite, Shift+Enter face rând nou — convenția din orice chat.
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.#send(); }
      },
    });
    this.sendEl = el('button', { class: 'chat-send', title: 'Отправить', onclick: () => this.#send() },
      [icon('send', 14)]);
    this.composerEl = el('div', { class: 'chat-composer' }, [this.inputEl, this.sendEl]);

    this.camereEl = el('div', { class: 'chat-camere' });

    this.root.append(
      el('div', { class: 'chat-head' }, [
        this.titluEl = el('h2', { class: 'chat-title',
          text: this.camera ? `Чат · ${this.camera}` : 'Общий чат' }),
        el('span', { class: 'chat-dot' }),
        this.onlineEl,
        this.userBoxEl = el('div', { class: 'chat-user' }),
      ]),
      this.camereEl,
      el('div', { class: 'chat-body' }, [this.listEl, this.jumpEl]),
      this.composerEl,
    );
    host.append(this.root);

    // La intrare sau ieșire refacem socketul: cookie-ul de sesiune se trimite la
    // handshake, deci o conexiune deschisă înainte de autentificare rămâne anonimă
    // oricât de mult te-ai autentifica după aceea.
    // Mesaje trimise din screener, cât timp chatul e deschis într-o filă.
    this.offShare = bus.on('chat:send', (payload) => this.#sendShared(payload));
    // Din grafic: „Обсуждение SOLUSDT" deschide direct camera monedei.
    this.offRoom = bus.on('chat:room', ({ symbol }) => this.#deschideCamera(symbol || ''));

    this.offSession = bus.on('session:change', () => {
      this.#renderUserBox();
      this.#syncComposer();
      this.#reconnect();
    });
    this.#renderUserBox();
    this.#syncComposer();

    await this.#loadHistory();
    if (this.camera) this.#deschideCamera(this.camera);
    this.#connect();
  }

  // ------------------------------------------------------------- încărcare

  async #loadHistory() {
    try {
      const data = await (await fetch('/api/chat/history')).json();
      this.messages = data.items || [];
      this.lastRead = data.last_read || 0;
      this.#setOnline(data.online || 0);
    } catch {
      this.messages = [];
    }
    this.#render();
    this.#scrollToBottom();
    this.#markRead();
  }

  #reconnect() {
    clearTimeout(this.reconnect);
    if (this.ws) {
      this.ws.onclose = null;        // închiderea asta e voită, nu o cădere de reparat
      this.ws.close();
    }
    this.#connect();
  }

  #connect() {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.ws = new WebSocket(`${proto}://${location.host}/ws/chat`);
    // Mesajul venit din screener înainte ca socketul să fie gata: îl trimitem la
    // deschidere, altfel s-ar pierde tăcut exact în cazul cel mai frecvent.
    this.ws.onopen = () => {
      const inAsteptare = this.app.pendingChat;
      if (!inAsteptare) return;
      this.app.pendingChat = null;
      this.#sendShared(inAsteptare);
    };
    this.ws.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      if (data.type === 'message') this.#append(data.message);
      else if (data.type === 'reactions') this.#applyReactions(data.message_id, data.reactions);
      else if (data.type === 'presence') this.#setOnline(data.online);
      else if (data.type === 'error') toast(data.detail, 'error');
    };
    // O reconectare simplă: chatul local cade doar dacă serverul repornește.
    this.ws.onclose = () => {
      if (this.destroyed) return;
      this.reconnect = setTimeout(() => this.#connect(), 2500);
    };
  }

  // ------------------------------------------------------------- randare

  #render() {
    clear(this.listEl);
    let ziua = null;
    let liniaPusa = false;

    const vizibile = this.#vizibile();
    for (const msg of vizibile) {
      const zi = dayLabel(msg.created_at);
      if (zi !== ziua) {
        ziua = zi;
        this.listEl.append(el('div', { class: 'chat-day', text: zi }));
      }
      // Linia se pune o singură dată, înaintea primului mesaj necitit al altcuiva.
      if (!liniaPusa && this.lastRead && msg.id > this.lastRead
          && msg.author.id !== session.user?.id) {
        liniaPusa = true;
        this.listEl.append(el('div', { class: 'chat-unread-line' }, [
          el('span', { text: 'Непрочитанные' }),
        ]));
      }
      this.listEl.append(this.#messageEl(msg));
    }

    if (!vizibile.length) {
      this.listEl.append(el('div', {
        class: 'chat-empty',
        text: this.camera
          ? `О ${this.camera} ещё никто не писал. Начните разговор.`
          : 'Сообщений пока нет. Напишите первое.',
      }));
    }
    this.#randCamere();
  }

  #messageEl(msg) {
    const meu = msg.author.id === session.user?.id;
    const reactBar = el('div', { class: 'chat-reactions' });

    const node = el('div', {
      class: `chat-msg${meu ? ' mine' : ''}`,
      dataset: { id: String(msg.id) },
      // Clic-dreapta deschide reacțiile, ca la ei — nu un meniu de browser.
      oncontextmenu: (e) => { e.preventDefault(); this.#openReactions(msg.id, e); },
    }, [
      // Acelaşi avatar ca pe profil şi în lentă — o singură regulă pentru tot.
      (() => {
        const a = el('span', { class: 'chat-avatar',
          style: { background: msg.author.status.color } });
        pictAvatar(a, msg.author, 34);
        return a;
      })(),
      el('div', { class: 'chat-body-col' }, [
        el('div', { class: 'chat-meta' }, [
          el('button', {
            class: 'chat-author',
            style: { color: msg.author.status.color },
            text: msg.author.username,
            title: `${msg.author.status.name} · рейтинг ${msg.author.rating}`,
            onclick: () => bus.emit('profile:open', { username: msg.author.username }),
          }),
          msg.author.role !== 'member'
            ? el('span', { class: 'chat-role', text: msg.author.role === 'owner' ? 'Владелец' : 'Админ' })
            : null,
          el('span', { class: 'chat-time', text: timeLabel(msg.created_at) }),
        ]),
        msg.symbol
          ? el('button', {
            class: 'chat-symbol', title: 'Открыть в скринере',
            onclick: () => bus.emit('symbol:open', { symbol: msg.symbol }),
          }, [icon('chartLine', 10), el('span', { text: msg.symbol.replace(/USDT$/, '') })])
          : null,
        msg.text ? el('div', { class: 'chat-text', text: msg.text }) : null,
        // Captura trimisă din screener: clic deschide imaginea la mărime întreagă.
        msg.shot
          ? el('img', {
            class: 'chat-shot', src: msg.shot, alt: msg.symbol || '',
            onclick: () => window.open(msg.shot, '_blank'),
          })
          : null,
        reactBar,
      ]),
    ]);

    node._reactBar = reactBar;
    this.#paintReactions(reactBar, msg.id, msg.reactions || {});
    return node;
  }

  #paintReactions(bar, messageId, reactions) {
    clear(bar);
    for (const [emoji, users] of Object.entries(reactions)) {
      if (!users.length) continue;
      const alMeu = session.user && users.includes(session.user.username);
      bar.append(el('button', {
        class: `chat-react${alMeu ? ' mine' : ''}`,
        title: users.join(', '),          // la trecerea cu mouse-ul se vede cine a pus
        onclick: () => this.#react(messageId, emoji),
      }, [
        el('span', { text: emoji }),
        el('span', { class: 'chat-react-n', text: String(users.length) }),
      ]));
    }
  }

  #applyReactions(messageId, reactions) {
    const msg = this.messages.find((m) => m.id === messageId);
    if (msg) msg.reactions = reactions;
    const node = this.listEl.querySelector(`.chat-msg[data-id="${messageId}"]`);
    if (node?._reactBar) this.#paintReactions(node._reactBar, messageId, reactions);
  }

  #openReactions(messageId, event) {
    if (!session.authenticated) { openAuth(); return; }
    document.querySelector('.chat-picker')?.remove();
    const picker = el('div', { class: 'chat-picker' }, EMOJI.map((e) => el('button', {
      class: 'chat-picker-btn', text: e,
      onclick: () => { this.#react(messageId, e); picker.remove(); },
    })));
    document.body.append(picker);
    // Ținem paleta în ecran: lângă marginea dreaptă ar ieși pe jumătate afară.
    const w = picker.offsetWidth;
    picker.style.left = `${Math.min(event.clientX, window.innerWidth - w - 12)}px`;
    picker.style.top = `${Math.min(event.clientY, window.innerHeight - 60)}px`;
    setTimeout(() => {
      const off = (e) => {
        if (!picker.contains(e.target)) { picker.remove(); document.removeEventListener('mousedown', off); }
      };
      document.addEventListener('mousedown', off);
    }, 0);
  }

  #react(messageId, emoji) {
    if (!session.authenticated) { openAuth(); return; }
    this.ws?.send(JSON.stringify({ type: 'react', message_id: messageId, emoji }));
  }

  // --------------------------------------------------------------- camere

  /** Mesajele camerei deschise. */
  #vizibile() {
    if (!this.camera) return this.messages;
    return this.messages.filter((m) => (m.symbol || '') === this.camera);
  }

  /**
   * Fasia de camere: „общий" plus monedele despre care s-a scris de curand.
   *
   * Camerele nu se creeaza si nu se sterg de nimeni — apar fiindca cineva a scris
   * despre o moneda si dispar cand discutia se pierde din istoric. O lista de camere
   * pe care trebuie sa le administreze cineva se umple cu camere goale.
   */
  #randCamere() {
    const monede = [];
    for (const m of this.messages) {
      if (m.symbol && !monede.includes(m.symbol)) monede.push(m.symbol);
    }
    // Moneda deschisa acum in screener e mereu in fasie, chiar daca n-are niciun mesaj:
    // altfel n-ai de unde incepe prima discutie despre ea.
    const curenta = this.app?.symbol;
    if (curenta && !monede.includes(curenta)) monede.unshift(curenta);
    // Camera deschisa e mereu in fasie, chiar daca n-are niciun mesaj: altfel butonul
    // apasat nu se vede nicaieri si pare ca n-ai selectat nimic.
    if (this.camera && !monede.includes(this.camera)) monede.unshift(this.camera);

    const semnatura = `${this.camera}|${monede.join(',')}`;
    if (semnatura === this.semnaturaCamere) return;
    this.semnaturaCamere = semnatura;

    clear(this.camereEl);
    const face = (val, eticheta, n) => el('button', {
      class: `chat-camera${this.camera === val ? ' on' : ''}`,
      onclick: () => this.#deschideCamera(val),
    }, [
      el('span', { text: eticheta }),
      n ? el('span', { class: 'chat-camera-n', text: String(n) }) : null,
    ]);

    this.camereEl.append(face('', 'Общий', 0));
    for (const sym of monede.slice(0, 12)) {
      this.camereEl.append(face(sym, sym.replace(/USDT$/, ''),
        this.messages.filter((m) => m.symbol === sym).length));
    }
  }

  #deschideCamera(symbol) {
    this.camera = symbol || '';
    this.semnaturaCamere = null;
    this.titluEl.textContent = this.camera ? `Чат · ${this.camera}` : 'Общий чат';
    this.inputEl.placeholder = this.camera
      ? `Что думаете по ${this.camera.replace(/USDT$/, '')}?` : 'Написать сообщение…';
    this.#render();
    this.#scrollToBottom();
  }

  // ------------------------------------------------------------- trimitere

  #send() {
    if (!session.authenticated) { openAuth(); return; }
    const text = this.inputEl.value.trim();
    if (!text) return;
    if (this.ws?.readyState !== WebSocket.OPEN) { toast('Нет связи с чатом', 'error'); return; }
    // Mesajul scris intr-o camera poarta moneda ei: altfel ar cadea in „общий" si
    // discutia despre o moneda s-ar rupe in doua la prima reincarcare.
    this.ws.send(JSON.stringify({ type: 'message', text, symbol: this.camera }));
    this.inputEl.value = '';
    this.#autoGrow();
  }

  /** Mesaj venit din screener («Поделиться» → «В чат»), cu captura graficului. */
  #sendShared({ text, symbol, shot }) {
    if (this.ws?.readyState !== WebSocket.OPEN) { toast('Нет связи с чатом', 'error'); return; }
    this.ws.send(JSON.stringify({ type: 'message', text: text || '', symbol, shot }));
  }

  #append(msg) {
    const jos = this.#atBottom();
    this.messages.push(msg);
    if (this.messages.length > 400) this.messages.shift();
    this.#render();
    // Doar dacă erai deja jos: altfel te-am smulge din locul unde citeai.
    if (jos || msg.author.id === session.user?.id) {
      this.#scrollToBottom();
      this.#markRead();
    } else {
      this.jumpEl.classList.remove('hidden');
    }
  }

  #autoGrow() {
    this.inputEl.style.height = 'auto';
    this.inputEl.style.height = `${Math.min(this.inputEl.scrollHeight, 140)}px`;
  }

  // ------------------------------------------------------------- derulare

  #atBottom() {
    const e = this.listEl;
    return e.scrollHeight - e.scrollTop - e.clientHeight < STICK_PX;
  }

  #scrollToBottom(mark = false) {
    this.listEl.scrollTop = this.listEl.scrollHeight;
    this.jumpEl.classList.add('hidden');
    if (mark) this.#markRead();
  }

  #onScroll() {
    if (this.#atBottom()) {
      this.jumpEl.classList.add('hidden');
      this.#markRead();
    }
  }

  #markRead() {
    const ultim = this.messages[this.messages.length - 1];
    if (!ultim || !session.authenticated || ultim.id <= this.lastRead) return;
    this.lastRead = ultim.id;
    fetch('/api/chat/read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message_id: ultim.id }),
    }).catch(() => {});
  }

  // ------------------------------------------------------------- utilizator

  #setOnline(n) {
    this.onlineEl.textContent = `${n} в сети`;
  }

  #renderUserBox() {
    clear(this.userBoxEl);
    if (!session.authenticated) {
      this.userBoxEl.append(el('button', {
        class: 'btn primary', text: 'Войти', onclick: () => openAuth(),
      }));
      return;
    }
    const u = session.user;
    this.userBoxEl.append(
      (() => {
        const a = el('span', { class: 'chat-avatar sm', style: { background: u.status.color } });
        pictAvatar(a, u, 24);
        return a;
      })(),
      el('span', { class: 'chat-me' }, [
        el('span', { class: 'chat-me-nick', text: u.username, style: { color: u.status.color } }),
        el('span', { class: 'chat-me-status', text: `${u.status.name} · ${u.rating}` }),
      ]),
      el('button', {
        class: 'chat-logout', title: 'Выйти',
        onclick: async () => { await session.logout(); toast('Вы вышли', 'info'); },
      }, [icon('logout', 12)]),
    );
  }

  #syncComposer() {
    const on = session.authenticated;
    this.inputEl.disabled = !on;
    this.sendEl.disabled = !on;
    this.inputEl.placeholder = on ? 'Написать сообщение…' : 'Войдите, чтобы писать';
  }

  unmount() {
    this.destroyed = true;
    clearTimeout(this.reconnect);
    this.offShare?.();
    this.offSession?.();
    this.offRoom?.();
    this.ws?.close();
    this.root?.remove();
  }
}
